/**
 * SaveToVaultFolderDialog — save a COPY of an email attachment into a chosen
 * Vault folder. The copy is an independent document (its own bytes + row), so it
 * survives deletion of the source email — deleting the message only trashes the
 * message's own attachment document, never this saved copy.
 */

import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, FolderPlus } from 'lucide-react';
import { api, type VaultDocumentFolderDto } from '@/lib/api';
import { toast } from '@/components/ui/use-toast';

const ROOT = '__root__';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The open message the attachment belongs to. */
  messageId: string | null;
  /** The attachment to copy (null disables Save). */
  attachment: { attachmentId: string; fileName: string } | null;
}

export function SaveToVaultFolderDialog({ open, onOpenChange, messageId, attachment }: Props) {
  const [holderId, setHolderId] = useState<string | null>(null);
  const [folders, setFolders] = useState<VaultDocumentFolderDto[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setFolders(null);
    setSelected(null);
    setNewName('');
    void api
      .listMyVaultMemberships()
      .then(async (m) => {
        if (cancelled) return;
        const hid = m.personalHolder?.holderId ?? null;
        setHolderId(hid);
        if (!hid) {
          setFolders([]);
          return;
        }
        const res = await api.listVaultDocumentFolders(hid);
        if (!cancelled) setFolders(res.folders);
      })
      .catch(() => {
        if (!cancelled) setFolders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const save = async () => {
    if (!messageId || !attachment || !selected) return;
    setSaving(true);
    try {
      const target = selected === ROOT ? null : selected;
      await api.copyEmailMailboxAttachmentToFolder(messageId, attachment.attachmentId, target);
      const label =
        target === null
          ? 'Vault root'
          : (folders?.find((f) => f.folderId === target)?.name ?? 'folder');
      toast({ description: `Copied to ${label}` });
      onOpenChange(false);
    } catch (e) {
      toast({
        title: 'Couldn’t save to the Vault',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const createFolder = async () => {
    if (!holderId || !newName.trim()) return;
    setCreating(true);
    try {
      const { folder } = await api.createVaultDocumentFolder({ holderId, name: newName.trim() });
      setFolders((prev) => [...(prev ?? []), folder]);
      setSelected(folder.folderId);
      setNewName('');
    } catch (e) {
      toast({
        title: 'Couldn’t create folder',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  };

  // User-managed folders only (protected system folders aren't pickable here),
  // sorted by their full path for a stable, readable list.
  const visible = (folders ?? [])
    .filter((f) => !f.isProtected)
    .sort((a, b) => a.path.localeCompare(b.path));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="truncate pr-6">
            Save a copy of “{attachment?.fileName ?? 'attachment'}” to a Vault folder
          </DialogTitle>
        </DialogHeader>
        {folders === null ? (
          <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading folders…
          </div>
        ) : (
          <div className="space-y-2">
            <div className="max-h-64 overflow-y-auto rounded-md border">
              <label className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent/50">
                <input
                  type="radio"
                  name="vault-folder"
                  checked={selected === ROOT}
                  onChange={() => setSelected(ROOT)}
                />
                Vault root
              </label>
              {visible.map((f) => (
                <label
                  key={f.folderId}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-sm hover:bg-accent/50"
                >
                  <input
                    type="radio"
                    name="vault-folder"
                    checked={selected === f.folderId}
                    onChange={() => setSelected(f.folderId)}
                  />
                  <span className="truncate">{f.path}</span>
                </label>
              ))}
              {visible.length === 0 && (
                <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                  No folders yet — create one below.
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Input
                placeholder="New folder name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="shrink-0 gap-1"
                disabled={!newName.trim() || creating}
                onClick={createFolder}
              >
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FolderPlus className="h-4 w-4" />
                )}
                Create
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving || !selected || !attachment}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save a copy'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
